import {
  EntityStatusControlConfig,
  StatusConfig,
  ArchiveReasonConfig,
} from "../types";
import type { KanbanColumn } from "../../../types/pipeline.types";

/**
 * Job-specific status configuration
 * ADO Feature #4940: Job Status Workflow
 */

export const JOB_STATUS_CONFIG: Record<string, StatusConfig> = {
  role_in_preparation: {
    label: "Role in Preparation",
    color: "#8b5cf6",
    bgColor: "#f3e8ff",
  },
  find_candidates_passive: {
    label: "Find Candidates - Passive / AI",
    color: "#3b82f6",
    bgColor: "#dbeafe",
  },
  find_candidates_active: {
    label: "Find Candidates - Active",
    color: "#2563eb",
    bgColor: "#bfdbfe",
  },
  recruitment_in_progress: {
    label: "Recruitment in Progress",
    color: "#10b981",
    bgColor: "#d1fae5",
  },
  on_hold: { label: "On Hold", color: "#f59e0b", bgColor: "#fef3c7" },
  delivered: { label: "Delivered", color: "#14b8a6", bgColor: "#ccfbf1" },
  archived: { label: "Archived", color: "#6b7280", bgColor: "#f3f4f6" },
};

export const JOB_ARCHIVE_REASONS: Record<string, ArchiveReasonConfig> = {
  cancelled: {
    label: "Cancelled",
    description: "Client cancelled the request",
  },
  expired: { label: "Expired", description: "Request expired without filling" },
  filled_externally: {
    label: "Filled Externally",
    description: "Position filled outside HireFocuZ",
  },
  budget_cut: { label: "Budget Cut", description: "Budget constraints" },
  other: { label: "Other", description: "Other reason (specify in notes)" },
};

export const JOB_STATUS_ORDER = [
  "role_in_preparation",
  "find_candidates_passive",
  "find_candidates_active",
  "recruitment_in_progress",
  "on_hold",
  "delivered",
  "archived",
];

/**
 * Get reason why a job transition is not allowed
 */
export const getJobDisabledReason = (
  currentStatus: string,
  targetStatus: string,
): string | null => {
  if (currentStatus === targetStatus) {
    return "Current status";
  }

  if (currentStatus === "archived") {
    return "Cannot change from archived";
  }

  if (targetStatus === "archived") {
    return null; // Archive is always available
  }

  // The backend allowed-transitions endpoint is the source of truth.
  // This function only provides hints for statuses that are never reachable.
  const currentIndex = JOB_STATUS_ORDER.indexOf(currentStatus);
  const targetIndex = JOB_STATUS_ORDER.indexOf(targetStatus);

  // Forward: only allow next step (no skipping), except on_hold (reachable from multiple statuses)
  if (targetIndex > currentIndex + 1 && targetStatus !== "on_hold") {
    return "Requires intermediate steps";
  }

  return null;
};

export const JOB_KANBAN_COLUMNS: KanbanColumn[] = [
  {
    key: "role_in_preparation",
    label: "In Preparation",
    statuses: ["role_in_preparation"],
  },
  {
    key: "find_candidates_passive",
    label: "Find Passive / AI",
    statuses: ["find_candidates_passive"],
  },
  {
    key: "find_candidates_active",
    label: "Find Active",
    statuses: ["find_candidates_active"],
  },
  {
    key: "recruitment_in_progress",
    label: "In Progress",
    statuses: ["recruitment_in_progress"],
  },
  { key: "on_hold", label: "On Hold", statuses: ["on_hold"] },
  { key: "delivered", label: "Delivered", statuses: ["delivered"] },
  { key: "archived", label: "Archived", statuses: ["archived"] },
];

export const JOB_BOARD_HIDDEN_BY_DEFAULT = ["delivered", "archived"];

export const JOB_CONFIG: EntityStatusControlConfig = {
  statusConfig: JOB_STATUS_CONFIG,
  archiveReasons: JOB_ARCHIVE_REASONS,
  entityName: "Job",
  entityNamePlural: "Jobs",
  defaultStatus: "role_in_preparation",
  queryKeys: {
    entity: "job",
    entities: "jobs",
    transitions: "jobTransitions",
  },
};
